<template>
  <div class="sample-management">
    <div class="page-header">
      <h2>样衣记录</h2>
      <div class="header-actions">
        <a-button type="primary" @click="handleAdd">
          <template #icon>
            <PlusOutlined />
          </template>
          新增样衣
        </a-button>
        <a-button @click="loadSamples" :loading="loading">
          <template #icon>
            <ReloadOutlined />
          </template>
          刷新
        </a-button>
      </div>
    </div>

    <a-card class="filter-card">
      <a-form layout="inline">
        <a-form-item label="样衣名称">
          <a-input
            v-model:value="searchForm.name"
            placeholder="请输入样衣名称"
            allow-clear
            style="width: 200px"
          />
        </a-form-item>
        <a-form-item label="状态">
          <a-select
            v-model:value="searchForm.status"
            placeholder="全部"
            allow-clear
            style="width: 120px"
          >
            <a-select-option value="">全部</a-select-option>
            <a-select-option value="MAKING">制作中</a-select-option>
            <a-select-option value="COMPLETED">已完成</a-select-option>
            <a-select-option value="CANCELLED">已取消</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item label="创建日期">
          <a-range-picker
            v-model:value="searchForm.dateRange"
            format="YYYY-MM-DD"
            value-format="YYYYMMDD"
            style="width: 240px"
          />
        </a-form-item>
        <a-form-item>
          <a-space>
            <a-button type="primary" @click="handleSearch">
              <template #icon>
                <SearchOutlined />
              </template>
              查询
            </a-button>
            <a-button @click="handleReset">
              重置
            </a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>

    <a-card>
      <a-table
        :columns="columns"
        :data-source="samples"
        :loading="loading"
        :pagination="pagination"
        @change="handleTableChange"
        row-key="id"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'imageUrls'">
            <div v-if="parseImages(record.imageUrls).length > 0" class="image-cell">
              <a-image
                :src="$thumb(parseImages(record.imageUrls)[0])"
                :width="50"
                :height="50"
                :preview="{ src: parseImages(record.imageUrls)[0] }"
                style="object-fit: cover; border-radius: 4px;"
              />
              <span v-if="parseImages(record.imageUrls).length > 1" class="image-count">
                +{{ parseImages(record.imageUrls).length - 1 }}
              </span>
            </div>
            <span v-else style="color: #999">无照片</span>
          </template>
          <template v-if="column.key === 'patternFee'">
            <span v-if="record.patternFee != null">¥{{ record.patternFee }}</span>
          </template>
          <template v-if="column.key === 'productionFee'">
            <span v-if="record.productionFee != null">¥{{ record.productionFee }}</span>
          </template>
          <template v-if="column.key === 'status'">
            <a-tag :color="statusColor(record.status)">
              {{ statusText(record.status) }}
            </a-tag>
          </template>
          <template v-if="column.key === 'completedDate'">
            {{ formatDate(record.completedDate) }}
          </template>
          <template v-if="column.key === 'createTime'">
            {{ formatDate(record.createTime) }}
          </template>
          <template v-if="column.key === 'action'">
            <a-space>
              <a-button type="link" size="small" @click="handleEdit(record)">
                编辑
              </a-button>
              <a-popconfirm
                title="确定要删除这条样衣记录吗？"
                @confirm="handleDelete(record)"
              >
                <a-button type="link" size="small" danger>
                  删除
                </a-button>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>
    </a-card>

    <a-modal
      v-model:open="modalVisible"
      :title="modalTitle"
      @ok="handleSubmit"
      @cancel="handleCancel"
      :confirm-loading="submitting"
      width="700px"
    >
      <a-form
        ref="formRef"
        :model="formData"
        :label-col="{ span: 5 }"
        :wrapper-col="{ span: 17 }"
      >
        <a-form-item
          label="样衣名称"
          name="name"
          :rules="[
            { required: true, message: '请输入样衣名称' },
            { max: 200, message: '长度不超过200个字符' }
          ]"
        >
          <a-input v-model:value="formData.name" placeholder="请输入样衣名称" />
        </a-form-item>
        <a-form-item label="尺码" name="sizes">
          <a-select
            v-model:value="formData.sizes"
            mode="multiple"
            placeholder="请选择尺码"
            :options="sizeOptions.map(s => ({ label: s, value: s }))"
            style="width: 100%"
          />
        </a-form-item>
        <a-form-item label="照片" name="imageUrls">
          <a-upload
            list-type="picture-card"
            v-model:file-list="fileList"
            :before-upload="beforeUpload"
            @remove="handleRemove"
            :multiple="true"
            accept="image/*"
          >
            <div v-if="fileList.length < 9">
              <PlusOutlined />
              <div style="margin-top: 8px">上传</div>
            </div>
          </a-upload>
        </a-form-item>
        <a-form-item label="打版费" name="patternFee">
          <a-input
            v-model:value="formData.patternFee"
            placeholder="请输入打版费"
            style="width: 200px"
            @input="formData.patternFee = formData.patternFee.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')"
          />
        </a-form-item>
        <a-form-item label="制作费" name="productionFee">
          <a-input
            v-model:value="formData.productionFee"
            placeholder="请输入制作费"
            style="width: 200px"
            @input="formData.productionFee = formData.productionFee.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')"
          />
        </a-form-item>
        <a-form-item label="打版师" name="patternMaker">
          <a-input v-model:value="formData.patternMaker" placeholder="请输入打版师名称" />
        </a-form-item>
        <a-form-item
          label="状态"
          name="status"
          :rules="[{ required: true, message: '请选择状态' }]"
        >
          <a-select v-model:value="formData.status" style="width: 200px">
            <a-select-option value="MAKING">制作中</a-select-option>
            <a-select-option value="COMPLETED">已完成</a-select-option>
            <a-select-option value="CANCELLED">已取消</a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item v-if="formData.status === 'COMPLETED'" label="完成时间" name="completedDate">
          <a-date-picker
            v-model:value="formData.completedDate"
            format="YYYY-MM-DD"
            value-format="YYYYMMDD"
            placeholder="请选择完成时间"
            style="width: 200px"
          />
        </a-form-item>
        <a-form-item label="备注" name="remark" :rules="[{ max: 500, message: '长度不超过500个字符' }]">
          <a-textarea
            v-model:value="formData.remark"
            placeholder="请输入备注"
            :rows="3"
          />
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import { ref, shallowRef, reactive, computed, onMounted } from 'vue'
import { message } from 'ant-design-vue'
import {
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons-vue'
import { sampleApi, uploadApi, productApi } from '@/api'

export default {
  name: 'SampleList',
  components: {
    PlusOutlined,
    ReloadOutlined,
    SearchOutlined
  },
  setup() {
    const loading = ref(false)
    const submitting = ref(false)
    const samples = shallowRef([])  // 纯展示大列表：shallowRef 免深度响应式
    const modalVisible = ref(false)
    const formRef = ref(null)
    const editingId = ref(null)
    const fileList = ref([])
    const sizeOptions = ref([])

    const loadSizeOptions = async () => {
      try {
        const result = await productApi.getAttributes('size')
        if (result.code === '0' && result.data) {
          sizeOptions.value = result.data.map(a => a.value)
        }
      } catch (error) {
        console.error('加载尺码选项失败:', error)
      }
    }

    const searchForm = reactive({
      name: '',
      status: undefined,
      dateRange: null
    })

    const formData = reactive({
      name: '',
      sizes: [],
      imageUrls: '',
      patternFee: '',
      productionFee: '',
      patternMaker: '',
      status: 'MAKING',
      completedDate: '',
      remark: ''
    })

    const pagination = reactive({
      current: 1,
      pageSize: 10,
      total: 0,
      showSizeChanger: true,
      showTotal: (total) => `共 ${total} 条`
    })

    const columns = [
      {
        title: '照片',
        key: 'imageUrls',
        width: 80
      },
      {
        title: '样衣名称',
        dataIndex: 'name',
        key: 'name',
        width: 160
      },
      {
        title: '尺码',
        dataIndex: 'sizes',
        key: 'sizes',
        width: 120
      },
      {
        title: '打版师',
        dataIndex: 'patternMaker',
        key: 'patternMaker',
        width: 100
      },
      {
        title: '打版费',
        key: 'patternFee',
        width: 100
      },
      {
        title: '制作费',
        key: 'productionFee',
        width: 100
      },
      {
        title: '状态',
        key: 'status',
        width: 90
      },
      {
        title: '完成时间',
        dataIndex: 'completedDate',
        key: 'completedDate',
        width: 120
      },
      {
        title: '创建时间',
        dataIndex: 'createTime',
        key: 'createTime',
        width: 140
      },
      {
        title: '备注',
        dataIndex: 'remark',
        key: 'remark',
        ellipsis: true
      },
      {
        title: '操作',
        key: 'action',
        width: 130,
        fixed: 'right'
      }
    ]

    const modalTitle = computed(() => {
      return editingId.value ? '编辑样衣' : '新增样衣'
    })

    const formatDate = (val) => {
      if (!val) return ''
      if (val.length === 8) return `${val.slice(0,4)}-${val.slice(4,6)}-${val.slice(6,8)}`
      if (val.length >= 14) return `${val.slice(0,4)}-${val.slice(4,6)}-${val.slice(6,8)} ${val.slice(8,10)}:${val.slice(10,12)}`
      return val
    }

    const statusColor = (status) => {
      const map = { MAKING: 'blue', COMPLETED: 'green', CANCELLED: 'red' }
      return map[status] || 'default'
    }

    const statusText = (status) => {
      const map = { MAKING: '制作中', COMPLETED: '已完成', CANCELLED: '已取消' }
      return map[status] || status
    }

    const parseImages = (imageUrls) => {
      if (!imageUrls) return []
      try {
        const arr = JSON.parse(imageUrls)
        return arr.map(item => typeof item === 'string' ? item : item.url)
      } catch {
        return []
      }
    }

    const loadSamples = async () => {
      loading.value = true
      try {
        const params = {
          name: searchForm.name || undefined,
          status: searchForm.status || undefined,
          pageNo: pagination.current,
          pageSize: pagination.pageSize
        }
        if (searchForm.dateRange && searchForm.dateRange.length === 2) {
          params.startDate = searchForm.dateRange[0]
          params.endDate = searchForm.dateRange[1]
        }
        const result = await sampleApi.list(params)

        if (result.code === '0') {
          samples.value = result.data.records || []
          pagination.total = result.data.total || 0
        } else {
          message.error(result.msg || '加载样衣列表失败')
        }
      } catch (error) {
        console.error('加载样衣列表失败:', error)
        message.error('加载样衣列表失败')
      } finally {
        loading.value = false
      }
    }

    const handleTableChange = (pag) => {
      pagination.current = pag.current
      pagination.pageSize = pag.pageSize
      loadSamples()
    }

    const handleSearch = () => {
      pagination.current = 1
      loadSamples()
    }

    const handleReset = () => {
      searchForm.name = ''
      searchForm.status = undefined
      searchForm.dateRange = null
      pagination.current = 1
      loadSamples()
    }

    const resetForm = () => {
      formData.name = ''
      formData.sizes = []
      formData.imageUrls = ''
      formData.patternFee = ''
      formData.productionFee = ''
      formData.patternMaker = ''
      formData.status = 'MAKING'
      formData.completedDate = ''
      formData.remark = ''
      fileList.value = []
      imageUrlList.value = []
      editingId.value = null
    }

    const handleAdd = () => {
      resetForm()
      modalVisible.value = true
    }

    const handleEdit = (record) => {
      editingId.value = record.id
      formData.name = record.name || ''
      formData.sizes = record.sizes ? record.sizes.split(',') : []
      formData.imageUrls = record.imageUrls || ''
      formData.patternFee = record.patternFee != null ? String(record.patternFee) : ''
      formData.productionFee = record.productionFee != null ? String(record.productionFee) : ''
      formData.patternMaker = record.patternMaker || ''
      formData.status = record.status || 'MAKING'
      formData.completedDate = record.completedDate || ''
      formData.remark = record.remark || ''

      // Restore file list and imageUrlList from imageUrls
      const images = parseImages(record.imageUrls)
      imageUrlList.value = [...images]
      fileList.value = images.map((url, index) => ({
        uid: `-${index}`,
        name: `image-${index}`,
        status: 'done',
        url: url
      }))

      modalVisible.value = true
    }

    const handleDelete = async (record) => {
      try {
        const result = await sampleApi.delete(record.id)
        if (result.code === '0') {
          message.success('删除成功')
          loadSamples()
        } else {
          message.error(result.msg || '删除失败')
        }
      } catch (error) {
        console.error('删除失败:', error)
        message.error('删除失败')
      }
    }

    const imageUrlList = ref([])

    const beforeUpload = async (file) => {
      const isImage = file.type.startsWith('image/')
      if (!isImage) {
        message.error('只能上传图片文件')
        return false
      }
      const isLt5M = file.size / 1024 / 1024 < 5
      if (!isLt5M) {
        message.error('图片大小不能超过5MB')
        return false
      }
      try {
        const result = await uploadApi.uploadImage(file)
        if (result.data && result.data.url) {
          imageUrlList.value.push(result.data.url)
          formData.imageUrls = JSON.stringify(imageUrlList.value)
        } else {
          message.error('上传失败：无效的响应')
        }
        return false
      } catch (error) {
        message.error('上传失败')
        return false
      }
    }

    const handleRemove = (file) => {
      const index = fileList.value.findIndex(item => item.uid === file.uid)
      if (index > -1) {
        imageUrlList.value.splice(index, 1)
        formData.imageUrls = imageUrlList.value.length > 0 ? JSON.stringify(imageUrlList.value) : ''
      }
    }

    const handleSubmit = async () => {
      try {
        await formRef.value.validateFields()

        submitting.value = true

        const submitData = {
          name: formData.name,
          sizes: formData.sizes.length > 0 ? formData.sizes.join(',') : undefined,
          imageUrls: formData.imageUrls || undefined,
          patternFee: formData.patternFee ? parseFloat(formData.patternFee) : undefined,
          productionFee: formData.productionFee ? parseFloat(formData.productionFee) : undefined,
          patternMaker: formData.patternMaker || undefined,
          status: formData.status,
          completedDate: formData.status === 'COMPLETED' ? formData.completedDate : undefined,
          remark: formData.remark || undefined
        }

        let result
        if (editingId.value) {
          result = await sampleApi.update({
            id: editingId.value,
            ...submitData
          })
        } else {
          result = await sampleApi.create(submitData)
        }

        if (result.code === '0') {
          message.success(editingId.value ? '更新成功' : '创建成功')
          modalVisible.value = false
          loadSamples()
        } else {
          message.error(result.msg || '操作失败')
        }
      } catch (error) {
        if (error.errorFields) {
          return
        }
        console.error('操作失败:', error)
        message.error('操作失败')
      } finally {
        submitting.value = false
      }
    }

    const handleCancel = () => {
      modalVisible.value = false
      resetForm()
    }

    onMounted(() => {
      loadSamples()
      loadSizeOptions()
    })

    return {
      loading,
      submitting,
      samples,
      modalVisible,
      modalTitle,
      formRef,
      formData,
      searchForm,
      columns,
      pagination,
      fileList,
      sizeOptions,
      formatDate,
      statusColor,
      statusText,
      parseImages,
      loadSamples,
      handleTableChange,
      handleSearch,
      handleReset,
      handleAdd,
      handleEdit,
      handleDelete,
      handleSubmit,
      handleCancel,
      beforeUpload,
      handleRemove
    }
  }
}
</script>

<style scoped>
.sample-management {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.filter-card {
  margin-bottom: 16px;
}

.image-cell {
  display: flex;
  align-items: center;
  gap: 4px;
}

.image-count {
  font-size: 12px;
  color: #999;
}
</style>
